import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
    Button,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import AllOrdersGrid from "../components/Shipping/AllOrdersGrid.jsx";
import ManualAddOrderDialog from "../components/Shipping/ManualAddOrderDialog.jsx";
import GridFilterBar, { useGridFilter } from "../components/Helpers/GridFilterBar.jsx";
import PullOrdersSplitButton from "../components/Helpers/PullOrdersSplitButton.jsx";
import { ORDER_FILTER_FIELDS } from "../components/Shipping/orderFilterFields.js";
import TabNavigation from "../components/TabNavigation.jsx";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
} from "../components/Products/catalogGridStyles.js";
import useApiData from "../services/useApiData.js";
import PostData from "../services/PostData.js";

const ORDER_TABS = [
    { label: "All Orders", value: 0, channel: null },
    { label: "eBay", value: 1, channel: "ebay" },
    { label: "Walmart", value: 2, channel: "walmart" },
    { label: "Shopify", value: 3, channel: "shopify" },
];

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultPullRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 3);
    return { startDate: dayInputValue(start), endDate: dayInputValue(end) };
}

function pullRangeError(startDate, endDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        return "Choose a start and end date.";
    }
    if (startDate > endDate) return "Start date must be on or before the end date.";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (days > 62) return "Date range cannot be longer than 62 days.";
    return "";
}

export default function Orders() {
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        activeConditions,
        conjunction: gridConjunction,
        barProps: orderFilterBar,
    } = useGridFilter(ORDER_FILTER_FIELDS);
    const [value, setValue] = useState(() => {
        const tab = parseInt(searchParams.get("t"), 10);
        return ORDER_TABS.some((item) => item.value === tab) ? tab : 0;
    });
    const initialRange = defaultPullRange();
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState("");
    const [pullMessage, setPullMessage] = useState("");
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const [searchInput, setSearchInput] = useState("");
    const [searchQ, setSearchQ] = useState("");
    const [manualAddOpen, setManualAddOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [rowCount, setRowCount] = useState(0);
    const [sortForApi, setSortForApi] = useState({ sort: "orderDate", sortDir: "desc" });
    const handleSortForApi = useCallback((next) => {
        setSortForApi((prev) =>
            prev.sort === next.sort && prev.sortDir === next.sortDir ? prev : next
        );
    }, []);
    const rangeError = pullRangeError(startDate, endDate);

    const orderNumber = searchParams.get("order") || "";
    const fromReturn = searchParams.get("fromReturn") || "";
    const fromShipment = searchParams.get("fromShipment") || "";

    useEffect(() => {
        const t = setTimeout(() => {
            setSearchQ(searchInput.trim());
            setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
        }, 300);
        return () => clearTimeout(t);
    }, [searchInput]);

    const filtersParam = useMemo(() => {
        if (!activeConditions.length) return "";
        return JSON.stringify({
            conjunction: gridConjunction,
            conditions: activeConditions.map(({ field, operator, value: v }) => ({
                field,
                operator,
                value: v,
            })),
        });
    }, [activeConditions, gridConjunction]);

    useEffect(() => {
        setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [filtersParam, value, orderNumber, sortForApi.sort, sortForApi.sortDir]);

    const activeTab = ORDER_TABS.find((item) => item.value === value) ?? ORDER_TABS[0];

    const listUrl = useMemo(() => {
        const params = new URLSearchParams();
        params.set("page", String(paginationModel.page + 1));
        params.set("pageSize", String(paginationModel.pageSize));
        params.set("sort", sortForApi.sort);
        params.set("sortDir", sortForApi.sortDir);
        if (orderNumber) params.set("order", orderNumber);
        else if (activeTab.channel) params.set("channel", activeTab.channel);
        if (searchQ) params.set("q", searchQ);
        if (filtersParam) params.set("filters", filtersParam);
        return `api/broylesllc/orders?${params.toString()}`;
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        orderNumber,
        activeTab.channel,
        searchQ,
        filtersParam,
        sortForApi.sort,
        sortForApi.sortDir,
    ]);

    const { data, error, isLoading, load, refetch } = useApiData(listUrl);
    const orders = useMemo(() => {
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data)) return data;
        return [];
    }, [data]);

    useEffect(() => {
        setRowCount(
            typeof data?.total === "number"
                ? data.total
                : Array.isArray(data)
                  ? data.length
                  : 0
        );
    }, [data]);

    const handleChange = (_event, newValue) => {
        setValue(newValue);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("t", String(newValue));
            return next;
        });
    };

    const clearOrderFilter = () => {
        setSearchParams(fromReturn ? { fromReturn } : {});
    };

    const pullOrders = async (action) => {
        if (rangeError) return;
        setPulling(true);
        setPullError("");
        setPullMessage("");
        try {
            const response = await PostData(
                { action, startDate, endDate, syncStatuses: true },
                "broylesllc/shipping/get-orders",
                null
            );
            if (!response?.success) {
                const message = response?.error?.response?.data?.message;
                setPullError(message || "Could not pull orders for that date range.");
                return;
            }
            const summary = response?.data || {};
            const parts = [];
            if (summary.ebay != null) {
                parts.push(
                    summary.ebay === "failed" ? "eBay failed" : `${summary.ebay} eBay`
                );
            }
            if (summary.walmart != null) {
                parts.push(
                    summary.walmart === "failed"
                        ? "Walmart failed"
                        : `${summary.walmart} Walmart`
                );
            }
            if (summary.shopify != null) {
                parts.push(
                    summary.shopify === "failed"
                        ? "Shopify failed"
                        : `${summary.shopify} Shopify`
                );
            }
            const warningText = Array.isArray(summary.warnings) && summary.warnings.length
                ? ` ${summary.warnings.join("; ")}.`
                : "";
            setPullMessage(
                parts.length
                    ? `Pulled ${parts.join(", ")} for ${startDate}–${endDate} (ET).${warningText}`
                    : `Pull finished for ${startDate}–${endDate} (ET).${warningText}`
            );
            await refetch?.();
        } finally {
            setPulling(false);
        }
    };

    useEffect(() => {
        const raw = searchParams.get("t");
        const tabFromUrl = parseInt(raw, 10);
        const valid = ORDER_TABS.some((item) => item.value === tabFromUrl);
        if (valid) {
            if (tabFromUrl !== value) setValue(tabFromUrl);
            return;
        }
        if (value !== 0) setValue(0);
        if (raw !== "0") {
            const next = { t: "0" };
            if (searchParams.get("order")) next.order = searchParams.get("order");
            if (searchParams.get("fromReturn")) next.fromReturn = searchParams.get("fromReturn");
            if (searchParams.get("fromShipment")) next.fromShipment = searchParams.get("fromShipment");
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams, value]);

    return (
        <CatalogPageShell
            title="Orders"
            subtitle="All Orders — filter with field conditions, combined with and or or."
        >
            {error ? (
                <p style={{ color: "#b91c1c", padding: "8px 16px", margin: 0 }}>
                    Failed to load orders.
                </p>
            ) : null}
            <div className="app-page-stack">
                {orderNumber || fromReturn || fromShipment ? (
                    <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                        {fromReturn ? (
                            <Button
                                size="small"
                                component={RouterLink}
                                to="/returns"
                                startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
                                sx={catalogQuietButtonSx}
                            >
                                Back to returns
                            </Button>
                        ) : null}
                        {fromShipment ? (
                            <Button
                                size="small"
                                component={RouterLink}
                                to="/order-shipping"
                                startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
                                sx={catalogQuietButtonSx}
                            >
                                Back to shipping
                            </Button>
                        ) : null}
                        {orderNumber ? (
                            <span className="catalog-selection-hint">
                                Order {orderNumber}
                                {!load && !isLoading && orders.length === 0
                                    ? " is not in the orders list yet."
                                    : ""}
                            </span>
                        ) : null}
                        {orderNumber ? (
                            <Button size="small" onClick={clearOrderFilter} sx={catalogQuietButtonSx}>
                                Show all orders
                            </Button>
                        ) : null}
                    </div>
                ) : null}
                <div style={{ flexShrink: 0 }}>
                    <TabNavigation
                        value={value}
                        onChange={handleChange}
                        tabs={ORDER_TABS}
                    />
                </div>
                <GridFilterBar {...orderFilterBar} />
                <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                    <TextField
                        size="small"
                        placeholder="Search order, buyer, item, tracking…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <div className="catalog-toolbar-spacer" />
                    {pullError ? (
                        <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                            {pullError}
                        </Typography>
                    ) : pullMessage ? (
                        <Typography component="span" sx={{ color: "#0f766e", fontSize: 13 }}>
                            {pullMessage}
                        </Typography>
                    ) : (
                        <span className="catalog-selection-hint">
                            {rowCount.toLocaleString()} order{rowCount === 1 ? "" : "s"}
                            {searchQ || activeConditions.length ? " matching" : ""}
                        </span>
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddOutlinedIcon fontSize="small" />}
                        onClick={() => setManualAddOpen(true)}
                        sx={catalogPrimaryButtonSx}
                    >
                        Manual Add Order
                    </Button>
                    <PullOrdersSplitButton
                        pulling={pulling}
                        onPull={pullOrders}
                        startDate={startDate}
                        endDate={endDate}
                        onStartChange={setStartDate}
                        onEndChange={setEndDate}
                        rangeError={rangeError}
                        hint="Updates Platform Status and keeps Bread Bskt order status."
                    />
                </div>
                <AllOrdersGrid
                    orders={orders}
                    loading={Boolean(load || isLoading || pulling)}
                    onCancelled={refetch}
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortForApi={handleSortForApi}
                />
            </div>
            <ManualAddOrderDialog
                open={manualAddOpen}
                onClose={() => setManualAddOpen(false)}
                onCreated={() => {
                    setPaginationModel((prev) =>
                        prev.page === 0 ? prev : { ...prev, page: 0 }
                    );
                    refetch?.();
                }}
            />
        </CatalogPageShell>
    );
}
